/**
 * STANDARDIZED CALCULATION SERVICE
 *
 * This service provides consistent calculation formulas across all calculators on the website.
 * All calculators MUST use these functions to ensure consistent results.
 *
 * Last Updated: 2026-05-20 (v4.7.87 — revised payout strategy + per-plant production model)
 *
 * REVISED PRODUCTION MODEL (overrides all prior assumptions):
 *   Per-plant monthly production = 100 TPH × 10 hrs/day × 22.4 days/month × 0.9 g/tonne
 *                                ≈ 20.16 kg gold / month per 100tph plant.
 *   Gold price held at $134,000/kg for projections.
 *   Net margin after 5% refining + 45% opex = 50%.
 *
 * PAYOUT DISPLAY (January 2027 onward):
 *   - July 2026 and October 2026 are not shown. The first payouts on the calculator are January 2027 and April 2027.
 *   - 2026 is a setup year with no public payout card.
 *   - Dividend per share = that year's profit ÷ shares sold so far for January and April 2027.
 *     The 1,400,000 figure remains the share-supply cap. It is not the divisor.
 */

// ============================================================================
// CONSTANTS - Single source of truth for all calculations
// ============================================================================

export const CALCULATION_CONSTANTS = {
 // Share Structure
 TOTAL_SHARES: 1_400_000,

 // Mining Operations (revised single-100tph-plant baseline)
 HECTARES_PER_PLANT: 25,
 PLANT_CAPACITY_TPH: 100,
 EFFECTIVE_HOURS_PER_DAY: 10,
 OPERATING_DAYS_PER_MONTH: 22.4,
 OPERATING_DAYS_PER_YEAR: 268.8, // 22.4 × 12
 BULK_DENSITY_T_PER_M3: 1.8, // retained for legacy callers; unused by new formula

 // Financial Assumptions
 GOLD_PRICE_USD_PER_KG: 134_000,
 REFINING_PERCENT: 5,
 OPEX_PERCENT: 45,

 // Geological / Recovery Parameters
 // Grade is now treated as grams of recovered gold per tonne of throughput
 // (recovery already baked in), per the revised per-plant production model.
 DEFAULT_GRAVEL_THICKNESS_M: 0.8,
 DEFAULT_IN_SITU_GRADE_G_PER_TONNE: 0.9,
 DEFAULT_RECOVERY_FACTOR_PERCENT: 100,
} as const;

// Per-plant monthly gold production in kilograms.
// Formula: TPH × hours/day × days/month × grade(g/t) / 1000
// 100 × 10 × 22.4 × 0.9 / 1000 = 20.16 kg/month
export const GOLD_KG_PER_MONTH_PER_PLANT =
 (CALCULATION_CONSTANTS.PLANT_CAPACITY_TPH *
  CALCULATION_CONSTANTS.EFFECTIVE_HOURS_PER_DAY *
  CALCULATION_CONSTANTS.OPERATING_DAYS_PER_MONTH *
  CALCULATION_CONSTANTS.DEFAULT_IN_SITU_GRADE_G_PER_TONNE) / 1000;

// Payouts before the offer is fully sold use the live share count.
// July 2027 is when the 1,400,000 shares are projected to be sold.
// January and April 2027 are still on the shares-sold split.
export const DYNAMIC_DENOMINATOR_PAYOUTS: ReadonlyArray<{ year: number; month: string }> = [
 { year: 2027, month: 'January' },
 { year: 2027, month: 'April' },
];

// ============================================================================
// ROI-TRIGGERED DENOMINATOR EXPANSION (Option B — v4.7.99)
//
// Cumulative dividend-per-share modelling against the static 1,400,000 cap
// shows initial shareholders reach the $1,000 ROI threshold during Q1 2029
// ($978.48 cumulative at end-2028 → $1,302.48 cumulative after Jan 2029 payout).
// By the close of 2029 the cumulative reaches $2,274.48/share — 2.27× the
// $1,000 cost basis — at which point the projection denominator expands from
// 1,400,000 to 3,000,000 effective 1 January 2030. This represents the
// company's roadmap to issue additional shares to fund the 200+ plant
// expansion AFTER the initial 1.4M holders have recovered their cost basis.
//
// IMPORTANT: This expansion only changes the PROJECTION DENOMINATOR used to
// compute forward-looking estimated dividend-per-share figures. The actual
// share supply cap (CALCULATION_CONSTANTS.TOTAL_SHARES = 1,400,000) is
// UNCHANGED — share transfers, ownership %, and the 19-phase sell-through
// continue to use the 1.4M cap as the live supply ceiling.
// ============================================================================

export const STATIC_DIVIDEND_DENOMINATOR = 1_400_000;
export const EXPANDED_DIVIDEND_DENOMINATOR = 3_000_000;
export const DENOMINATOR_EXPANSION_YEAR = 2029;

/**
 * Returns the projection denominator for a calendar year after the
 * shares-sold payouts. That denominator is 1,400,000.
 * January 2027 and April 2027 are not this year-level figure.
 * Those payouts use the shares sold so far.
 *
 * Used by computeDividendProjection() inside this service AND by display
 * surfaces (FinancialDataPage, CalculatorPage, DividendsSection) that need
 * to back-calculate the year-specific profit pool from the per-share figure.
 */
export function getDividendDenominatorForYear(_year: number): number {
 return STATIC_DIVIDEND_DENOMINATOR;
}

/**
 * Plain-English notice for the share split used in the projection.
 * From July 2027 onward the divisor is 1,400,000.
 */
export const ROI_EXPANSION_NOTICE =
 'From July 2027 onward, dividends are split between 1,400,000 shares. January 2027 and April 2027 are split by the shares sold so far. Projection only — not a guarantee.';

export function isDynamicDenominatorPayout(year: number, month: string): boolean {
 return DYNAMIC_DENOMINATOR_PAYOUTS.some(p => p.year === year && p.month === month);
}

// Payout schedule. 2026 has no public payout. 2027 onward follows Jan/Apr/Jul/Oct.
// A payout is considered past once the month following it has begun.
const PAYOUT_MONTH_INDEX: Record<string, number> = {
 January: 0, April: 3, July: 6, October: 9,
};

export interface UpcomingPayout {
 year: number;
 month: string; // 'January' | 'April' | 'July' | 'October'
 isDynamicDenominator: boolean;
}

const SCHEDULED_PAYOUTS: ReadonlyArray<{ year: number; month: string }> = [
 // 2027+ runs the full quarterly cadence. October 2026 is not shown.
 ...[2027, 2028, 2029, 2030, 2031, 2032, 2033, 2034, 2035].flatMap(y =>
 ['January', 'April', 'July', 'October'].map(month => ({ year: y, month }))
 ),
];

export function getNextDividendPayout(now: Date = new Date()): UpcomingPayout {
 for (const p of SCHEDULED_PAYOUTS) {
 // Rollover boundary: first day of the month AFTER the payout month
 const monthIdx = PAYOUT_MONTH_INDEX[p.month];
 const rolloverYear = p.month === 'October' ? p.year + 1 : p.year;
 const rolloverMonth = p.month === 'October' ? 0 : monthIdx + 1;
 const rolloverDate = new Date(rolloverYear, rolloverMonth, 1, 0, 0, 0, 0);
 if (now < rolloverDate) {
 return { ...p, isDynamicDenominator: isDynamicDenominatorPayout(p.year, p.month) };
 }
 }
 const last = SCHEDULED_PAYOUTS[SCHEDULED_PAYOUTS.length - 1];
 return { ...last, isDynamicDenominator: isDynamicDenominatorPayout(last.year, last.month) };
}

// ============================================================================
// EXPANSION PLAN - 5-Year Growth Projections (2026-2030)
// ============================================================================

// EXPANSION_PLAN values are the OPERATIONAL ROADMAP targets (aspirational
// year-end deployed plant counts), not the conservative dividend baseline.
// The conservative dividend baseline lives in PRODUCTION_TARGETS below and
// underpins DIVIDEND_PROJECTIONS — 2026 dividend math is based on a single
// 100 TPH plant. Each additional plant adds ~20.16 kg/month linearly to the
// total annual yield and the resulting per-share dividend.
export const EXPANSION_PLAN = {
 2026: { plants: 1, hectares: 25, description: 'One 100 TPH plant on the property' },
 2027: { plants: 20, hectares: 500, description: '10 plants by July, 20 plants by December' },
 2028: { plants: 30, hectares: 750, description: '30 plants by July' },
 2029: { plants: 100, hectares: 2500, description: 'Major Growth Phase' },
 2030: { plants: 200, hectares: 5000, description: 'Full Scale Operations' },
} as const;

// Engineering lead time per custom-built plant. To meet the aggressive
// 2026-2027 expansion targets, multiple engineering firms manufacture the
// proprietary 100 TPH plant model in parallel.
export const PLANT_ENGINEERING_LEAD_TIME_DAYS = 45;

// ============================================================================
// PRODUCTION TARGETS - Projected gold output per year
// Derived from per-plant baseline (GOLD_KG_PER_MONTH_PER_PLANT = 20.16 kg)
// scaled by the operating-plant count for each year.
//
// 2026 is a setup year. No public payout is shown. The first public payouts
// are January 2027 and April 2027 (reported under year 2027).
// ============================================================================

const PRODUCTION_TARGETS: Record<number, {
 plants: number;
 monthsOperating: number; // operating months counted toward this year's payouts
 payoutMonths: string[];
 payoutType: 'quarterly' | 'semiAnnual';
 note?: string;
}> = {
 // Setup year. No public 2026 payout card. First payouts are January and April 2027.
 2026: {
 plants: 1,
 monthsOperating: 0,
 payoutMonths: [],
 payoutType: 'quarterly',
 note: 'One 100 TPH plant is on the property and ready for production as soon as the yellow machines are on the land. No dividend payout is shown for 2026. The first payouts shown are January 2027 and April 2027. Profit for those two periods is divided by the shares sold so far.',
 },
 2027: {
 plants: 20,
 monthsOperating: 12,
 payoutMonths: ['January', 'April', 'July', 'October'],
 payoutType: 'quarterly',
 note: 'January and April stay on the one plant on site and are split by shares sold so far. July uses 10 plants. October uses 20 plants, the December rate. From July the split is 1,400,000 shares.',
 },
 2028: {
 plants: 30,
 monthsOperating: 12,
 payoutMonths: ['January', 'April', 'July', 'October'],
 payoutType: 'quarterly',
 note: 'January and April use 20 plants. July and October use 30 plants. The split is 1,400,000 shares.',
 },
 2029: { plants: 100, monthsOperating: 12, payoutMonths: ['January', 'April', 'July', 'October'], payoutType: 'quarterly', note: 'From July 2027 onward, dividends are split across 1,400,000 shares. Projection only.' },
 2030: { plants: 200, monthsOperating: 12, payoutMonths: ['January', 'April', 'July', 'October'], payoutType: 'quarterly' },
 // 2031–2034 linearly interpolated between 2030 (200 plants) and 2035 (400 plants),
 // +40 plants per year. Quarterly payout cadence preserved.
 2031: { plants: 240, monthsOperating: 12, payoutMonths: ['January', 'April', 'July', 'October'], payoutType: 'quarterly' },
 2032: { plants: 280, monthsOperating: 12, payoutMonths: ['January', 'April', 'July', 'October'], payoutType: 'quarterly' },
 2033: { plants: 320, monthsOperating: 12, payoutMonths: ['January', 'April', 'July', 'October'], payoutType: 'quarterly' },
 2034: { plants: 360, monthsOperating: 12, payoutMonths: ['January', 'April', 'July', 'October'], payoutType: 'quarterly' },
 2035: { plants: 400, monthsOperating: 12, payoutMonths: ['April', 'October'], payoutType: 'semiAnnual', note: 'Long-term projection' },
 // 2036 continues the +40-plants/year trajectory; semi-annual cadence matches 2035.
 2036: { plants: 440, monthsOperating: 12, payoutMonths: ['April', 'October'], payoutType: 'semiAnnual', note: 'Long-term projection' },
};

// ============================================================================
// DIVIDEND PROJECTIONS - Calculated from production formula
// Formula: Gross Revenue = Gold(kg) × Market Price($/kg)
// Profit = Gross Revenue × (1 - Refining% - Opex%)  // 50% net margin
// Screens must use computeDynamicAnnualProjection().
// January 2027 and April 2027 divide by shares sold so far.
// From July 2027 onward the divisor is 1,400,000.
// ============================================================================

// Plant count in force for each payout quarter. Each quarter is 3 months.
// January and April 2027 stay on the one plant already on site.
// July 2027 is 10 plants. October 2027 is the December rate of 20 plants.
// July 2028 steps to 30 plants and October 2028 holds 30.
const QUARTER_RAMP: Record<number, { month: string; plants: number }[]> = {
 2027: [
  { month: 'January', plants: 1 },
  { month: 'April', plants: 1 },
  { month: 'July', plants: 10 },
  { month: 'October', plants: 20 },
 ],
 2028: [
  { month: 'January', plants: 20 },
  { month: 'April', plants: 20 },
  { month: 'July', plants: 30 },
  { month: 'October', plants: 30 },
 ],
};

function quartersForYear(year: number, target?: typeof PRODUCTION_TARGETS[number]) {
 const ramp = QUARTER_RAMP[year];
 if (ramp) return ramp.map(q => ({ month: q.month, plants: q.plants, months: 3 }));
 if (!target || target.payoutMonths.length === 0) return [];
 const monthsEach = target.monthsOperating / target.payoutMonths.length;
 return target.payoutMonths.map(month => ({ month, plants: target.plants, months: monthsEach }));
}

export function projectionYearNeedsLiveShares(year: number): boolean {
 const target = PRODUCTION_TARGETS[year];
 return quartersForYear(year, target).some(q => isDynamicDenominatorPayout(year, q.month));
}

function fixedPayoutDivisor(_year: number): number {
 return STATIC_DIVIDEND_DENOMINATOR;
}

function emptyYearProjection(note?: string) {
 return {
 goldProductionKgPerMonth: 0,
 goldProductionKgPerYear: 0,
 goldProductionKgAnnualizedCapacity: 0,
 monthsOperating: 0,
 isRampYear: true,
 estimatedDividendPerShare: 0,
 projectionDenominator: getDividendDenominatorForYear(2026),
 quarterlyPayouts: [] as { month: string; amount: number; note: string }[],
 ...(note ? { note } : {}),
 };
}

function computeDividendProjection(year: number, target: typeof PRODUCTION_TARGETS[number]) {
 const { plants, monthsOperating, payoutMonths, payoutType, note } = target;
 const rampQuarters = quartersForYear(year, target);
 if (rampQuarters.length === 0 && payoutMonths.length === 0) {
 return emptyYearProjection(note);
 }
 const kgPerYear = QUARTER_RAMP[year]
  ? rampQuarters.reduce((sum, q) => sum + q.plants * GOLD_KG_PER_MONTH_PER_PLANT * q.months, 0)
  : plants * GOLD_KG_PER_MONTH_PER_PLANT * monthsOperating;
 const kgPerMonth = QUARTER_RAMP[year] ? kgPerYear / (rampQuarters.length * 3) : plants * GOLD_KG_PER_MONTH_PER_PLANT;
 const grossRevenue = kgPerYear * CALCULATION_CONSTANTS.GOLD_PRICE_USD_PER_KG;
 const totalDeductions = (CALCULATION_CONSTANTS.REFINING_PERCENT + CALCULATION_CONSTANTS.OPEX_PERCENT) / 100;
 const profit = grossRevenue * (1 - totalDeductions);
 const denominator = getDividendDenominatorForYear(year);
 const operatingMonths = QUARTER_RAMP[year] ? rampQuarters.length * 3 : monthsOperating;

 const payouts = (QUARTER_RAMP[year] ? rampQuarters : payoutMonths.map(month => ({
  month,
  plants,
  months: operatingMonths / payoutMonths.length,
 }))).map(q => {
  const quarterKg = q.plants * GOLD_KG_PER_MONTH_PER_PLANT * q.months;
  const quarterProfit = quarterKg * CALCULATION_CONSTANTS.GOLD_PRICE_USD_PER_KG * (1 - totalDeductions);
  const live = isDynamicDenominatorPayout(year, q.month);
  const amount = live ? 0 : Math.round((quarterProfit / fixedPayoutDivisor(year)) * 100) / 100;
  return { month: q.month, amount, note: '±' as const };
 });

 const roundedDividend = QUARTER_RAMP[year]
  ? Math.round(payouts.reduce((sum, p) => sum + p.amount, 0) * 100) / 100
  : Math.round((profit / denominator) * 100) / 100;

 const base: any = {
 goldProductionKgPerMonth: Math.round(kgPerMonth * 100) / 100,
 // For ramp years this is kg captured in the year's payouts.
 goldProductionKgPerYear: Math.round(kgPerYear * 100) / 100,
 goldProductionKgAnnualizedCapacity: Math.round(kgPerMonth * 12 * 100) / 100,
 monthsOperating: operatingMonths,
 isRampYear: Boolean(QUARTER_RAMP[year]) || operatingMonths < 12,
 estimatedDividendPerShare: roundedDividend,
 projectionDenominator: denominator,
 };

 if (payoutType === 'semiAnnual') {
 base.semiAnnualPayouts = payouts;
 } else {
 base.quarterlyPayouts = payouts;
 }

 if (note) {
 base.note = note;
 }

 return base;
}

export const DIVIDEND_PROJECTIONS: Record<number, {
 goldProductionKgPerMonth: number;
 goldProductionKgPerYear: number;
 goldProductionKgAnnualizedCapacity: number;
 monthsOperating: number;
 isRampYear: boolean;
 estimatedDividendPerShare: number;
 projectionDenominator: number;
 quarterlyPayouts?: { month: string; amount: number; note: string }[];
 semiAnnualPayouts?: { month: string; amount: number; note: string }[];
 note?: string;
}> = Object.fromEntries(
 Object.entries(PRODUCTION_TARGETS).map(([year, target]) => {
  const yearNum = parseInt(year);
  return [yearNum, computeDividendProjection(yearNum, target)];
 })
);

export const DIVIDEND_DISCLAIMER =
 "All dividend estimates are projections based on anticipated mining production yield (gold extracted, processed, and sold). Actual dividends depend on mineral output, plant efficiency, gold market prices, and operating costs. Dividends are derived from mining profits and are not guaranteed. Past projections do not guarantee future results.";

// ============================================================================
// CORE CALCULATION FUNCTIONS
// ============================================================================

export interface GoldProductionInputs {
 landHa: number;
 avgGravelThickness?: number; // retained for legacy callers (unused by new formula)
 inSituGrade?: number;        // grams of recovered gold per tonne of throughput
 recoveryFactor?: number;     // retained for legacy callers (unused by new formula)
 goldPriceUsdPerKg?: number;
 opexPercent?: number;
}

export interface GoldProductionResults {
 numPlants: number;
 annualThroughputT: number;
 annualGoldKg: number;
 annualRevenue: number;
 annualRefiningCost: number;
 annualOperatingCost: number;
 annualEbit: number;
 dividendPerShare: number;
}

/**
 * Calculate gold production and financial metrics.
 * This is the MASTER calculation function — all calculators should use this.
 *
 * Revised throughput model (overrides prior volumetric/bulk-density model):
 *   per-plant kg/month = TPH × hrs/day × days/month × grade(g/t) / 1000
 *   100 × 10 × 22.4 × 0.9 / 1000 = 20.16 kg/month per 100tph plant
 */
export function calculateGoldProduction(inputs: GoldProductionInputs): GoldProductionResults {
 const {
 landHa,
 inSituGrade = CALCULATION_CONSTANTS.DEFAULT_IN_SITU_GRADE_G_PER_TONNE,
 goldPriceUsdPerKg = CALCULATION_CONSTANTS.GOLD_PRICE_USD_PER_KG,
 opexPercent = CALCULATION_CONSTANTS.OPEX_PERCENT,
 } = inputs;

 const numPlants = landHa / CALCULATION_CONSTANTS.HECTARES_PER_PLANT;

 const annualThroughputT =
 numPlants *
 CALCULATION_CONSTANTS.PLANT_CAPACITY_TPH *
 CALCULATION_CONSTANTS.EFFECTIVE_HOURS_PER_DAY *
 CALCULATION_CONSTANTS.OPERATING_DAYS_PER_YEAR;

 // Direct g/tonne model: recovery is already baked into the grade input
 const annualGoldKg = (annualThroughputT * inSituGrade) / 1000;

 const annualRevenue = annualGoldKg * goldPriceUsdPerKg;
 const annualRefiningCost = annualRevenue * (CALCULATION_CONSTANTS.REFINING_PERCENT / 100);
 const annualOperatingCost = annualRevenue * (opexPercent / 100);
 const annualEbit = annualRevenue - annualRefiningCost - annualOperatingCost;

 const dividendPerShare = annualEbit / CALCULATION_CONSTANTS.TOTAL_SHARES;

 return {
 numPlants,
 annualThroughputT,
 annualGoldKg,
 annualRevenue,
 annualRefiningCost,
 annualOperatingCost,
 annualEbit,
 dividendPerShare,
 };
}

function netProfitFromProjection(goldProductionKgPerYear: number): number {
 const gross = goldProductionKgPerYear * CALCULATION_CONSTANTS.GOLD_PRICE_USD_PER_KG;
 const keep = 1 - (CALCULATION_CONSTANTS.REFINING_PERCENT + CALCULATION_CONSTANTS.OPEX_PERCENT) / 100;
 return gross * keep;
}

/**
 * Per-share payout for one scheduled month.
 * Profit for the year is divided by the shares sold so far, then split
 * across that year's payout months. Returns zeros when the share count
 * is missing or the month is not on that year's schedule (July 2026 is not).
 */
export interface DynamicPayoutResult {
 year: number;
 month: string;
 isDynamic: boolean;
 staticPerShare: number;
 dynamicPerShare: number;
 perShare: number;
 totalSharesSoldNow: number;
 denominatorUsed: number;
}

export function getDynamicPayoutPerShare(
 year: number,
 month: string,
 totalSharesSoldNow: number,
): DynamicPayoutResult {
 const target = PRODUCTION_TARGETS[year];
 const quarter = quartersForYear(year, target).find(q => q.month === month);
 const validSold = Number.isFinite(totalSharesSoldNow) && totalSharesSoldNow > 0;
 const live = Boolean(quarter) && isDynamicDenominatorPayout(year, month);
 const divisor = !quarter ? null : live
  ? (validSold ? totalSharesSoldNow : null)
  : fixedPayoutDivisor(year);
 const profit = quarter
  ? netProfitFromProjection(quarter.plants * GOLD_KG_PER_MONTH_PER_PLANT * quarter.months)
  : 0;
 const perShare = divisor ? Math.round((profit / divisor) * 100) / 100 : 0;

 return {
  year,
  month,
  isDynamic: Boolean(live && divisor),
  staticPerShare: 0,
  dynamicPerShare: perShare,
  perShare,
  totalSharesSoldNow: validSold ? totalSharesSoldNow : 0,
  denominatorUsed: divisor || 0,
 };
}

/**
 * Shared user-facing copy for the dynamic-denominator notice. Use this in any
 * calculator/dashboard that displays the January 2027 and April 2027 payouts.
 */
export const DYNAMIC_DENOMINATOR_NOTICE =
 'January 2027 and April 2027 are divided by the shares sold so far. Thereafter, dividends are divided by 1,400,000 shares. These figures are projections, not a guaranteed payout.';

/**
 * Annual projection. Each payout uses its own plant count and divisor.
 * January 2027 and April 2027 need the live share count.
 * From July 2027 onward the divisor is 1,400,000.
 * Returns null when a payout needs the live share count and it is missing.
 */
export interface DynamicAnnualProjection {
 year: number;
 estimatedDividendPerShare: number;
 quarterlyPayouts?: { month: string; amount: number; note: string; isDynamic: boolean; plants: number; denominatorUsed: number }[];
 semiAnnualPayouts?: { month: string; amount: number; note: string; isDynamic: boolean; plants: number; denominatorUsed: number }[];
 denominatorUsed: number;
 staticDenominator: number;
 hasDynamicPayouts: boolean;
 scaleFactor: number;
}

export function computeDynamicAnnualProjection(
 year: number,
 totalSharesSoldNow: number,
): DynamicAnnualProjection | null {
 const base = DIVIDEND_PROJECTIONS[year as keyof typeof DIVIDEND_PROJECTIONS];
 const target = PRODUCTION_TARGETS[year];
 if (!base || !target) return null;

 const validSold = Number.isFinite(totalSharesSoldNow) && totalSharesSoldNow > 0;
 const quarters = quartersForYear(year, target);
 if (quarters.length === 0) {
  return {
   year,
   estimatedDividendPerShare: 0,
   quarterlyPayouts: [],
   denominatorUsed: 0,
   staticDenominator: fixedPayoutDivisor(year),
   hasDynamicPayouts: false,
   scaleFactor: 1,
  };
 }

 const built: { month: string; amount: number; note: string; isDynamic: boolean; plants: number; denominatorUsed: number }[] = [];
 for (const quarter of quarters) {
  const live = isDynamicDenominatorPayout(year, quarter.month);
  const divisor = live
   ? (validSold ? totalSharesSoldNow : null)
   : fixedPayoutDivisor(year);
  if (!divisor) return null;
  const profit = netProfitFromProjection(quarter.plants * GOLD_KG_PER_MONTH_PER_PLANT * quarter.months);
  built.push({
   month: quarter.month,
   amount: Math.round((profit / divisor) * 100) / 100,
   note: '±',
   isDynamic: live,
   plants: quarter.plants,
   denominatorUsed: divisor,
  });
 }

 const annual = Math.round(built.reduce((sum, p) => sum + p.amount, 0) * 100) / 100;
 const quarterly = target.payoutType === 'quarterly' ? built : undefined;
 const semiAnnual = target.payoutType === 'semiAnnual' ? built : undefined;

 return {
  year,
  estimatedDividendPerShare: annual,
  quarterlyPayouts: quarterly,
  semiAnnualPayouts: semiAnnual,
  denominatorUsed: built.every(p => p.isDynamic)
   ? (validSold ? totalSharesSoldNow : 0)
   : fixedPayoutDivisor(year),
  staticDenominator: fixedPayoutDivisor(year),
  hasDynamicPayouts: built.some(p => p.isDynamic),
  scaleFactor: 1,
 };
}

/**
 * Shared user-facing copy for the scaling & expansion notice. Use this on any
 * calculator/projection surface that displays 2026 dividend projections so
 * investors understand the 1-plant conservative baseline and the multi-plant
 * scaling roadmap.
 */
export const PLANT_ON_SITE_NOTICE =
 '1 × 100 TPH plant is on the property and ready for production as soon as all the yellow machines required are on the land.';

export const SCALING_NOTICE = {
 baseline:
 '1 × 100 TPH plant is on the property and ready for production as soon as all the yellow machines required are on the land. The January 2027 and April 2027 projections use that one plant: about 20.16 kg of gold per month at 100 TPH × 10 hours × 22.4 days × 0.9 g/tonne.',
 scalingPotential:
 '2027 grows to 10 plants by July and 20 plants by December. July 2028 reaches 30 plants. Each additional active plant adds about 20.16 kg of gold per month.',
 leadTime:
 'Each custom-built 100 TPH plant carries a 45-day engineering lead time. To meet the 2026-2027 expansion targets, multiple engineering firms are manufacturing our proprietary plant model in parallel.',
} as const;

export const SCALING_NOTICE_SUMMARY =
 `Baseline: January 2027 and April 2027 use the 1 plant on site. 2027 grows to 10 plants by July and 20 plants by December. July 2028 reaches 30 plants. Each additional plant adds ~20.16 kg/month. Lead time: ${PLANT_ENGINEERING_LEAD_TIME_DAYS}-day engineering build per plant.`;

/**
 * Calculate user-specific dividend projections
 */
export function calculateUserDividends(userShares: number, year: number = 2026) {
 const yearData = EXPANSION_PLAN[year as keyof typeof EXPANSION_PLAN];
 if (!yearData) {
 throw new Error(`Invalid year: ${year}. Must be between 2026-2030.`);
 }

 const production = calculateGoldProduction({ landHa: yearData.hectares });
 
 const userAnnualDividend = production.dividendPerShare * userShares;
 const userMonthlyDividend = userAnnualDividend / 12;
 const userQuarterlyDividend = userAnnualDividend / 4;

 return {
 ...production,
 userShares,
 userAnnualDividend,
 userMonthlyDividend,
 userQuarterlyDividend,
 year,
 plants: yearData.plants,
 hectares: yearData.hectares,
 };
}

/**
 * Calculate share purchase details
 */
export function calculateSharePurchase(
 purchaseAmount: number,
 pricePerShare: number
) {
 const shares = Math.floor(purchaseAmount / pricePerShare);
 const totalCost = shares * pricePerShare;
 const remainingAmount = purchaseAmount - totalCost;

 return {
 purchaseAmount,
 shares,
 totalCost,
 remainingAmount,
 pricePerShare,
 };
}

/**
 * Calculate commission structure
 */
export function calculateCommission(
 purchaseAmount: number,
 phaseId: number
) {
 // Pre Sale (phase_id = 1): 15% USDT + 15% shares (single level)
 if (phaseId === 1) {
 const usdtCommission = purchaseAmount * 0.15;
 const shareCommission = purchaseAmount * 0.15;
 
 return {
 structure: 'Pre Sale',
 usdtCommission,
 shareCommission,
 levels: 1,
 description: '15% USDT + 15% shares (direct sponsor only)',
 };
 }
 
 // Phase 1+ (phase_id >= 2): Multi-level 10% USDT + 5% cascading shares
 return {
 structure: 'Multi-Level',
 level1: {
 usdtCommission: purchaseAmount * 0.10,
 shareCommission: purchaseAmount * 0.05,
 },
 level2: {
 usdtCommission: purchaseAmount * 0.005,
 shareCommission: purchaseAmount * 0.0025,
 },
 level3: {
 usdtCommission: purchaseAmount * 0.00025,
 shareCommission: purchaseAmount * 0.000125,
 },
 description: '10% USDT + 5% shares (cascading unlimited depth)',
 };
}

/**
 * Get dividend projection for a specific year
 */
export function getDividendProjection(year: number) {
 const projection = DIVIDEND_PROJECTIONS[year as keyof typeof DIVIDEND_PROJECTIONS];
 if (!projection) {
 throw new Error(`No dividend projection available for year ${year}`);
 }
 return projection;
}

